import {areWeTestingWithJest, isDevEnv} from "@buildwithflux/shared";
import {StoreApi, UseBoundStore, create} from "zustand";
import {devtools} from "zustand/middleware";
import {useShallow} from "zustand/react/shallow";

import {useFluxServices} from "injection/hooks";

import {DialogType, IDialog, IDialogProps} from "./types";

/**
 * State for managing open dialogs in the application
 *
 * This replaces the Redux dialogs reducer state, which tracked which dialogs
 * are currently open and their associated props.
 */
type DialogsStoreState = {
    /**
     * Array of currently opened dialogs
     */
    openedDialogs: IDialog<DialogType>[];

    /**
     * Opens a dialog of the specified type with optional props
     * If a dialog of the same type is already open, this is a no-op
     */
    openDialog: <T extends DialogType>(type: T, props?: IDialogProps<T>) => void;

    /**
     * Modifies the props of an already open dialog
     */
    modifyDialog: <T extends DialogType>(type: T, props?: IDialogProps<T>) => void;

    /**
     * Closes a dialog of the specified type
     * Only closes if the dialog is the topmost one (last in the array)
     */
    closeDialog: (type: DialogType) => void;

    /**
     * Closes all open dialogs
     */
    closeAllDialogs: () => void;
};

/**
 * The exported type of the store
 */
export type UseDialogsStore = UseBoundStore<StoreApi<DialogsStoreState>>;

/**
 * Creates the vanilla non-bound-to-React dialogs store
 *
 * This is a vanilla Zustand store, and is only exported for use in tests. In components, you'd usually use the hooks
 * below, and even when trying to access low-level details of the store, you'd use the bound version of this store.
 *
 * The store returned from this function cannot be directly used as a hook in a component. For that, see
 * the hooks below.
 */
export function createDialogsStore(): UseDialogsStore {
    return create<DialogsStoreState>()(
        devtools(
            (set, get): DialogsStoreState => {
                return {
                    openedDialogs: [],

                    openDialog: <T extends DialogType>(type: T, props?: IDialogProps<T>) => {
                        const currentDialogs = get().openedDialogs;
                        // Don't open if already open
                        if (currentDialogs.some((dialog) => dialog.type === type)) {
                            return;
                        }
                        set(
                            {
                                openedDialogs: [
                                    ...currentDialogs,
                                    {
                                        type,
                                        props: (props ?? {}) as IDialogProps<T>,
                                    } as IDialog<DialogType>,
                                ],
                            },
                            false,
                            "openDialog",
                        );
                    },

                    modifyDialog: <T extends DialogType>(type: T, props?: IDialogProps<T>) => {
                        const currentDialogs = get().openedDialogs;
                        const dialogIndex = currentDialogs.findIndex((dialog) => dialog.type === type);
                        if (dialogIndex === -1) {
                            return;
                        }
                        const newDialogs = [...currentDialogs];
                        newDialogs[dialogIndex] = {
                            ...newDialogs[dialogIndex]!,
                            props: props as IDialogProps<typeof type>,
                        };
                        set({openedDialogs: newDialogs}, false, "modifyDialog");
                    },

                    closeDialog: (type: DialogType) => {
                        const currentDialogs = get().openedDialogs;
                        // Only close if it's the topmost dialog
                        if (currentDialogs.length > 0 && currentDialogs[currentDialogs.length - 1]!.type === type) {
                            set({openedDialogs: currentDialogs.slice(0, -1)}, false, "closeDialog");
                        }
                    },

                    closeAllDialogs: () => {
                        const currentDialogs = get().openedDialogs;
                        if (currentDialogs.length > 0) {
                            set({openedDialogs: []}, false, "closeAllDialogs");
                        }
                    },
                };
            },
            {enabled: isDevEnv() && !areWeTestingWithJest(), name: "DialogsStore"},
        ),
    );
}

/**
 * Gets a reactive slice of the dialogs store: the array of opened dialogs
 * Uses shallow comparison to prevent unnecessary re-renders
 */
export function useOpenedDialogs(): IDialog<DialogType>[] {
    return useFluxServices().useDialogsStore(useShallow((state) => state.openedDialogs));
}

/**
 * Returns the function to open a dialog
 */
export function useOpenDialog(): <T extends DialogType>(type: T, props?: IDialogProps<T>) => void {
    return useFluxServices().useDialogsStore((state) => state.openDialog);
}

/**
 * Returns the function to modify a dialog's props
 */
export function useModifyDialog(): <T extends DialogType>(type: T, props?: IDialogProps<T>) => void {
    return useFluxServices().useDialogsStore((state) => state.modifyDialog);
}

/**
 * Returns the function to close a dialog
 */
export function useCloseDialog(): (type: DialogType) => void {
    return useFluxServices().useDialogsStore((state) => state.closeDialog);
}

/**
 * Returns the function to close all dialogs
 */
export function useCloseAllDialogs(): () => void {
    return useFluxServices().useDialogsStore((state) => state.closeAllDialogs);
}

/**
 * Checks if a specific dialog type is currently open
 */
export function useIsDialogOpen(type: DialogType): boolean {
    return useFluxServices().useDialogsStore((state) => state.openedDialogs.some((dialog) => dialog.type === type));
}

/**
 * Gets the props for a specific dialog type if it's open
 */
export function useDialogProps<T extends DialogType>(type: T): IDialogProps<T> | undefined {
    return useFluxServices().useDialogsStore((state) => {
        const dialog = state.openedDialogs.find((d) => d.type === type);
        return dialog?.props as IDialogProps<T> | undefined;
    });
}
